import React, { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ExternalLink, Loader2, X } from 'lucide-react';
import type { LibraryTarget } from './libraryLinks';

/**
 * Chat's side panel for BARK's Library (docs/specs/lookup.md, "Article
 * links"). An article link in an answer opens the Library at that article,
 * date and passage in a frame beside the chat; the reader can browse the
 * Library from there. Opt-in: only a screen that renders a LibraryPanelHost
 * with an origin gets it, so every other user of ChatMessageMarkdown keeps
 * plain EUR-Lex links.
 */

export interface LibraryLink {
    target: LibraryTarget;
    /** The link as the answer opens it today (EUR-Lex). */
    eurlex: string;
    /** What the panel's header says: the link's own text. */
    label: string;
}

interface LibraryPanelApi {
    origin: string;
    close: () => void;
    open: (link: LibraryLink, opener: HTMLElement) => void;
}

const LibraryPanelContext = createContext<LibraryPanelApi | null>(null);

/** The panel of the enclosing LibraryPanelHost; null where there is none. */
export const useLibraryPanel = () => useContext(LibraryPanelContext);

/** `sandbox` for the Library frame. `allow-same-origin` keeps the Library's
 *  own origin (it reads its own fragments and keeps pane widths in storage),
 *  never the app's. `allow-forms`: the Library's search and version picker
 *  are GET forms. `allow-popups` + `allow-popups-to-escape-sandbox`: its
 *  EUR-Lex and PDF links open a normal tab (as EURLEX_PREVIEW_SANDBOX,
 *  utils/findingSourceLinks.ts). Never `allow-top-navigation`: the Library
 *  must not navigate the app. */
export const LIBRARY_FRAME_SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox';

const BUTTON = 'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-fg-secondary hover:text-fg hover:bg-surface-sunken focus-visible:shadow-bark-focus';
const ICON_BUTTON = 'p-1 rounded-md text-fg-tertiary hover:text-fg hover:bg-surface-sunken focus-visible:shadow-bark-focus disabled:opacity-40 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-fg-tertiary';

/** What a Library embed page posts to its parent on every load and history
 *  change (docs/specs/regulation-browser.md, "Embed mode"). */
export const LIBRARY_PAGE_MESSAGE = 'bark-library-page';

/** A page the panel has shown. `title` comes from the page's own message. */
interface PanelPage { url: string; title?: string }

interface PanelState {
    link: LibraryLink;
    pages: PanelPage[];
    index: number;
    /** The URL the panel last loaded in the frame itself. Pages the reader
     *  reaches inside the frame never change it: setting `src` would load
     *  them a second time. */
    frame: string;
    /** Changes whenever the panel itself loads a page (open, Back, Forward):
     *  the frame is replaced, so the browser's own history gets no entries
     *  from the panel and Back / Forward are the panel's alone. */
    load: number;
}

/** The page title without the site's suffix ("Article 92 | BARK"). */
const pageTitle = (title?: string) => (title || '').replace(/\s*\|\s*BARK\s*$/, '').trim();

/** An embed URL → the same page in the full Library (a new tab). */
export function fullLibraryUrl(embedUrl: string): string {
    try {
        const url = new URL(embedUrl);
        url.pathname = url.pathname.replace(/^(\/[^/]+)\/embed(?=\/|$)/, '$1');
        return url.toString();
    } catch {
        return embedUrl;
    }
}

function originOf(url: string): string | null {
    try { return new URL(url).origin; } catch { return null; }
}

export function LibraryPanelHost({ origin, children, dock }: { origin: string; children: React.ReactNode;
    /** Opt-in for a workspace with its own far-right agent. Other hosts keep the chat split. */
    dock?: { right: number; onWidth: (width: number) => void };
}) {
    const [state, setState] = useState<PanelState | null>(null);
    const [loading, setLoading] = useState(false);
    const isOpen = state !== null;
    const panel = useRef<HTMLElement>(null);
    const onWidth = dock?.onWidth;
    useLayoutEffect(() => {
        if (!onWidth) return;
        const element = isOpen ? panel.current : null;
        const measure = () => onWidth(element && window.matchMedia('(min-width: 1024px)').matches
            ? Math.ceil(element.getBoundingClientRect().width) + 36 : 0);
        measure();
        const observer = element ? new ResizeObserver(measure) : null;
        if (element) observer?.observe(element);
        window.addEventListener('resize', measure);
        return () => { observer?.disconnect(); window.removeEventListener('resize', measure); onWidth(0); };
    }, [isOpen, onWidth]);
    // The panel loaded a page itself: the next page message names that page
    // (perhaps after a redirect, e.g. /concept/find → /concept/<id>), so it
    // replaces the current entry instead of adding one.
    const awaiting = useRef(false);
    const opener = useRef<HTMLElement | null>(null);
    const heading = useRef<HTMLHeadingElement>(null);
    const id = useId();
    const libraryOrigin = useMemo(() => originOf(origin), [origin]);

    // Another article link from the chat starts a new history at its page:
    // the header names the link the reader clicked, and Back walks the pages
    // reached from that link, never into an earlier link's reading (the chat
    // keeps every link one click away).
    const current = useRef<PanelState | null>(null);
    current.current = state;
    const open = useCallback((next: LibraryLink, from: HTMLElement) => {
        opener.current = from;
        const prev = current.current;
        if (prev && prev.pages.length === 1 && prev.pages[0].url === next.target.embed) {
            setState({ ...prev, link: next });
            return;
        }
        awaiting.current = true;
        setLoading(true);
        setState({ link: next, pages: [{ url: next.target.embed }], index: 0, frame: next.target.embed, load: (prev?.load ?? 0) + 1 });
    }, []);
    const close = useCallback(() => {
        awaiting.current = false;
        setState(null);
        const from = opener.current;
        opener.current = null;
        if (from && document.contains(from)) from.focus();
    }, []);
    const go = useCallback((step: number) => {
        const prev = current.current;
        if (!prev) return;
        const index = prev.index + step;
        if (index < 0 || index >= prev.pages.length) return;
        awaiting.current = true;
        setLoading(true);
        setState({ ...prev, index, frame: prev.pages[index].url, load: prev.load + 1 });
    }, []);
    const api = useMemo(() => (origin ? { origin, open, close } : null), [origin, open, close]);

    useEffect(() => { if (isOpen) heading.current?.focus(); }, [isOpen]);
    useEffect(() => {
        if (!isOpen) return;
        // A definition popover handles its own Escape first (it stops it).
        const escape = (event: KeyboardEvent) => { if (event.key === 'Escape' && !event.defaultPrevented) close(); };
        document.addEventListener('keydown', escape);
        return () => document.removeEventListener('keydown', escape);
    }, [isOpen, close]);
    useEffect(() => {
        if (!isOpen || !libraryOrigin) return;
        // Only the configured Library's own pages, and only this message.
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== libraryOrigin) return;
            const data = event.data as { type?: unknown; url?: unknown; title?: unknown } | null;
            if (!data || data.type !== LIBRARY_PAGE_MESSAGE || typeof data.url !== 'string') return;
            const url = data.url;
            if (originOf(url) !== libraryOrigin) return;
            const title = typeof data.title === 'string' ? data.title : undefined;
            const replace = awaiting.current;
            awaiting.current = false;
            setState((prev) => {
                if (!prev) return prev;
                const pages = prev.pages.slice();
                if (replace || pages[prev.index].url === url) {
                    pages[prev.index] = { url, title };
                    return { ...prev, pages };
                }
                return { ...prev, pages: [...pages.slice(0, prev.index + 1), { url, title }], index: prev.index + 1 };
            });
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [isOpen, libraryOrigin]);

    const link = state?.link ?? null;
    const page = state ? state.pages[state.index] : null;
    const title = pageTitle(page?.title) || link?.label || '';
    // Before its first message the start page is the link's own target.
    const full = state && page ? (state.index === 0 && !page.title ? link!.target.full : fullLibraryUrl(page.url)) : '';

    return (
        <LibraryPanelContext.Provider value={api}>
            <div className="h-full min-h-0 flex relative" style={dock ? {'--library-right': `${dock.right + 36}px`} as React.CSSProperties : undefined}>
                <div className="flex-1 min-w-0 h-full">{children}</div>
                {state && link && page && (
                    <aside ref={panel} aria-labelledby={`${id}-title`} className={`fixed inset-0 z-40 flex flex-col bg-surface text-fg lg:border-l lg:border-border-default ${dock
                        ? 'lg:absolute lg:inset-y-0 lg:left-auto lg:right-[var(--library-right)] lg:w-[min(42%,580px,calc(100%_-_var(--library-right)_-_320px))]'
                        : 'lg:static lg:z-auto lg:w-[min(50%,max(360px,calc(100%_-_var(--library-reserve,360px))))] lg:shrink-0'}`}>
                        <div className={`flex flex-wrap items-center border-b border-border-default ${dock ? 'gap-1.5 px-2 py-2' : 'gap-2 px-4 py-3'}`}>
                            <button type="button" aria-label="Back" title="Back" disabled={state.index === 0} onClick={() => go(-1)} className={ICON_BUTTON}>
                                <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
                            </button>
                            <button type="button" aria-label="Forward" title="Forward" disabled={state.index >= state.pages.length - 1} onClick={() => go(1)} className={ICON_BUTTON}>
                                <ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" />
                            </button>
                            <h2 id={`${id}-title`} ref={heading} tabIndex={-1} title={title} className="grow shrink basis-48 min-w-0 line-clamp-2 break-words text-sm font-semibold rounded focus:outline-none focus-visible:shadow-bark-focus">{title}</h2>
                            <a href={full} target="_blank" rel="noopener noreferrer" className={BUTTON}>
                                <ExternalLink size={14} strokeWidth={1.5} aria-hidden="true" /> Open in Library
                            </a>
                            <a href={link.eurlex} target="_blank" rel="noopener noreferrer" className={BUTTON}>
                                <ExternalLink size={14} strokeWidth={1.5} aria-hidden="true" /> Open on EUR-Lex
                            </a>
                            <button type="button" aria-label="Close Library" onClick={close} className="p-1 rounded-md text-fg-tertiary hover:text-fg hover:bg-surface-sunken focus-visible:shadow-bark-focus">
                                <X size={16} strokeWidth={1.5} aria-hidden="true" />
                            </button>
                        </div>
                        <div className="relative flex-1 min-h-0">
                            {loading && (
                                <p role="status" className="absolute inset-0 z-10 flex items-center justify-center gap-2 bg-surface text-sm text-fg-secondary">
                                    <Loader2 size={16} strokeWidth={1.5} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
                                    Loading the Library
                                </p>
                            )}
                            <iframe
                                key={state.load}
                                src={state.frame}
                                title={`Library: ${title}`}
                                sandbox={LIBRARY_FRAME_SANDBOX}
                                className="block w-full h-full border-0"
                                onLoad={() => setLoading(false)}
                            />
                        </div>
                    </aside>
                )}
            </div>
        </LibraryPanelContext.Provider>
    );
}
